import { useParams, Link } from 'react-router-dom';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NotFound from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryMap[slug] : undefined;

  if (!category) {
    return <NotFound />;
  }

  const categoryArticles = getArticlesByCategory(slug!);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 自慰方式完全指南`,
    description: category.description,
    url: `https://mastinfo.github.io/category/${slug}`,
    inLanguage: 'zh-CN',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Article',
          name: a.title,
          url: `https://mastinfo.github.io/article/${a.slug}`,
        },
      })),
    },
  };

  return (
    <>
      <SchemaOrg type="CollectionPage" additionalData={schemaData} />

      <div className="min-h-screen bg-background-50 pt-16">
        {/* Header */}
        <section className="w-full bg-gradient-to-br from-primary-50 via-background-50 to-accent-50/30 py-12 md:py-16">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
            <div className="mt-6">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${category.color}`}>
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                {category.name}
              </h1>
              <p className="text-foreground-600 max-w-2xl text-sm md:text-base leading-relaxed">
                {category.description}
              </p>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full bg-white border-b border-background-200 sticky top-16 z-40">
          <div className="max-w-6xl mx-auto px-4 md:px-6 overflow-x-auto">
            <div className="flex items-center gap-1 py-3 min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Article List */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-white rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime}</span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2" style={{ fontFamily: 'var(--font-heading)' }}>
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="mt-3 text-xs text-foreground-400">
                      {article.author}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {categoryArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                  <i className="ri-article-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}